import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import SignoutHeader from "../subcomponents/SignoutHeader";
import Typography from "@mui/material/Typography";
import {
  ActivePlanOfCare,
  AgentType,
  Consent,
  EHR,
  FrontendUser,
  Insurance,
  OmniPatient,
  Organization,
  PatientGroup,
  QueuePatient,
  QueuePatientState
} from "../types";
import {
  CircularProgress,
  IconButton,
  Modal,
  TextField,
  Tooltip,
  Snackbar,
  Alert,
  Button,
  InputAdornment,
  Chip,
  Menu,
  MenuItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Checkbox,
  Paper,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from "@mui/material";
import { Sidebar } from "../styles/GeneralStyles";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FilterListIcon from "@mui/icons-material/FilterList";
import HealthAndSafetyOutlinedIcon from "@mui/icons-material/HealthAndSafetyOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { UpdateWebptPatientModal } from "../forms/UpdatePatientModal";
import { WebptAddPatientModal } from "../forms/AddPatientModal";
import { RootState, useAppDispatch } from "../store";
import { useSelector } from "react-redux";
import { Colors } from "../Colors";
import {
  api,
  convertE164ToReadable
} from "../utils/utils";
import { countAvailableDays, filterPastAvailableDates } from "../utils/availableDates";
import {
  archivePatient,
  getWaitlistPatients,
  unarchivePatient,
  setIncludeArchivedPatients,
  restorePageFromCache,
  clearPageCache,
} from "../slices/PatientSlice";
import { getSession } from "../slices/AuthSlice";
import { DateTime } from "luxon";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import GroupIcon from "@mui/icons-material/Group";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { ArchivedPatientsModal } from "../components/modals/ArchivedPatientsModal";
import CustomAutocomplete from "../components/CustomAutocomplete";
import { DraggableList } from "../components/draggable-list/DraggableList";
import { DropResult } from "react-beautiful-dnd";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { updateOrganizationAttribute, setOrganizationAttribute } from "../slices/AuthSlice";
import { fetchInsurances } from "../slices/InsurancesSlice";

type SortField = "name" | "phone" | "waitlist" | "group" | "priority" | "createdAt";
type SortOrder = "asc" | "desc";

interface FilterState {
  status: "all" | "waitlist" | "no-waitlist";
  group: string | "all";
}

const NO_INSURANCE_VALUE = "__none__";

type InsuranceFilterOption = { label: string; value: string };

interface InsuranceFilterPickerProps {
  options: InsuranceFilterOption[];
  selected: string[];
  onChange: (next: string[]) => void;
}

const sameStringSet = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  for (const id of b) if (!set.has(id)) return false;
  return true;
};

const InsuranceFilterPicker: React.FC<InsuranceFilterPickerProps> = ({ options, selected, onChange }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<string[]>(selected);
  const [search, setSearch] = useState("");
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const open = !!anchor;

  useEffect(() => {
    if (open) setDraft(selected);
  }, [open, selected]);

  const total = options.length;
  const draftCount = draft.length;
  const selectedCount = selected.length;
  const draftSet = useMemo(() => new Set(draft), [draft]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return options.filter(opt => {
      if (showSelectedOnly && !draftSet.has(opt.value)) return false;
      if (needle && !opt.label.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [options, search, showSelectedOnly, draftSet]);

  const handleClose = () => {
    setAnchor(null);
    setSearch("");
    setShowSelectedOnly(false);
    if (!sameStringSet(draft, selected)) onChange(draft);
  };

  const toggle = (id: string) => {
    setDraft(prev => (prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]));
  };

  const hasSelection = selectedCount > 0;
  const triggerLabel = (() => {
    if (!hasSelection) return "Insurance";
    if (selectedCount === 1) {
      const match = options.find(o => o.value === selected[0]);
      return match ? match.label : "1 selected";
    }
    return `${selectedCount} selected`;
  })();

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<HealthAndSafetyOutlinedIcon sx={{ fontSize: 18 }} />}
        endIcon={
          hasSelection ? (
            <CloseIcon
              sx={{ fontSize: 16, opacity: 0.7, "&:hover": { opacity: 1 } }}
              onClick={(e) => {
                e.stopPropagation();
                onChange([]);
              }}
            />
          ) : (
            <ArrowDropDownIcon sx={{ fontSize: 20 }} />
          )
        }
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          textTransform: "none",
          fontSize: "0.875rem",
          maxWidth: 240,
          borderColor: hasSelection ? Colors.primary : "rgba(255, 255, 255, 0.2)",
          color: hasSelection ? Colors.primary : "#FFF",
          "& .MuiButton-endIcon": { ml: 0.5 },
          "&:hover": {
            borderColor: Colors.primary,
            backgroundColor: "rgba(94, 120, 238, 0.08)"
          }
        }}
      >
        <Typography
          component="span"
          sx={{
            fontSize: "0.875rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: 160
          }}
        >
          {triggerLabel}
        </Typography>
      </Button>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 0.5,
            width: 360,
            backgroundColor: Colors.bg3,
            borderRadius: "12px",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.3)"
          }
        }}
      >
        <Box sx={{ p: 1.5, borderBottom: "1px solid rgba(255, 255, 255, 0.08)", display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Typography sx={{ fontSize: "0.75rem", color: Colors.info, fontWeight: 500, letterSpacing: "0.02em" }}>
              {draftCount} of {total} selected
            </Typography>
            <Box sx={{ display: "flex", gap: 0.25 }}>
              {draftCount < total && (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setDraft(options.map(o => o.value))}
                  sx={{ textTransform: "none", fontSize: "0.72rem", minWidth: 0, padding: "0 6px", lineHeight: 1.4, color: Colors.primary }}
                >
                  Select all
                </Button>
              )}
              {draftCount > 0 && (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setDraft([])}
                  sx={{ textTransform: "none", fontSize: "0.72rem", minWidth: 0, padding: "0 6px", lineHeight: 1.4, color: Colors.info }}
                >
                  Clear
                </Button>
              )}
            </Box>
          </Box>
          <TextField
            size="small"
            placeholder="Search insurances…"
            value={search}
            autoFocus
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: Colors.info }} />
                </InputAdornment>
              )
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "rgba(255, 255, 255, 0.04)",
                fontSize: "0.875rem",
                color: "#FFF",
                "& fieldset": { borderColor: "rgba(255, 255, 255, 0.12)" },
                "&:hover fieldset": { borderColor: "rgba(255, 255, 255, 0.2)" },
                "&.Mui-focused fieldset": { borderColor: Colors.primary, borderWidth: "1px" }
              }
            }}
          />
          <Box sx={{ display: "flex", backgroundColor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255, 255, 255, 0.08)", borderRadius: "10px", p: "3px", gap: "2px" }}>
            {[
              { label: `All (${total})`, value: false as const },
              { label: `Selected (${draftCount})`, value: true as const }
            ].map(opt => {
              const active = showSelectedOnly === opt.value;
              return (
                <Box
                  key={String(opt.value)}
                  onClick={() => setShowSelectedOnly(opt.value)}
                  sx={{
                    flex: 1,
                    textAlign: "center",
                    py: 0.65,
                    borderRadius: "8px",
                    fontSize: "0.78rem",
                    fontWeight: active ? 600 : 400,
                    color: active ? "#FFF" : Colors.info,
                    backgroundColor: active ? "rgba(94, 120, 238, 0.18)" : "transparent",
                    cursor: "pointer",
                    userSelect: "none",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      color: "#FFF",
                      backgroundColor: active ? "rgba(94, 120, 238, 0.18)" : "rgba(255,255,255,0.05)"
                    }
                  }}
                >
                  {opt.label}
                </Box>
              );
            })}
          </Box>
        </Box>
        <Box sx={{ maxHeight: 320, overflowY: "auto", py: 0.5 }}>
          {filtered.length === 0 ? (
            <Box sx={{ px: 2, py: 3, textAlign: "center", color: Colors.info, fontSize: "0.8rem" }}>
              {showSelectedOnly && draftCount === 0 ? "Nothing selected yet." : "No matches."}
            </Box>
          ) : (
            filtered.map(opt => {
              const checked = draftSet.has(opt.value);
              const isNoneRow = opt.value === NO_INSURANCE_VALUE;
              return (
                <Box
                  key={opt.value}
                  onClick={() => toggle(opt.value)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    cursor: "pointer",
                    transition: "background-color 0.1s",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.04)" }
                  }}
                >
                  <Checkbox
                    checked={checked}
                    size="small"
                    sx={{
                      p: 0,
                      color: Colors.info,
                      "&.Mui-checked": { color: Colors.primary }
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.85rem",
                      color: "#FFF",
                      fontStyle: isNoneRow ? "italic" : "normal",
                      opacity: isNoneRow ? 0.85 : 1
                    }}
                  >
                    {opt.label}
                  </Typography>
                </Box>
              );
            })
          )}
        </Box>
      </Popover>
    </>
  );
};

const Patients: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const organization = useSelector((state: RootState) => state.auth.organization);
  const loadingPatients = useSelector((state: RootState) => state.patients.loadingFetchPatients);
  const includeArchivedPatients = useSelector((state: RootState) => state.patients.includeArchivedPatients);
  const serverTotal = useSelector((state: RootState) => state.patients.serverTotal);
  const nextCursor = useSelector((state: RootState) => state.patients.nextCursor);
  const currentCursor = useSelector((state: RootState) => state.patients.currentCursor);
  const pageCache = useSelector((state: RootState) => state.patients.pageCache);
  const allPatientsLoaded = useSelector((state: RootState) => state.patients.allPatientsLoaded);
  const agents = useSelector((state: RootState) => state.agents.agents);
  const waitlistAgent = useMemo(() => agents.find((agent) => agent.agentType === AgentType.waitlist), [agents]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  // cursors[i] = cursor used to fetch page i (undefined = start)
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);

  // Sorting state
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  // Tracks the sort order passed to the server (only changes when sorting by createdAt)
  const [serverSortOrder, setServerSortOrder] = useState<"asc" | "desc">("desc");

  // Filter state
  const [filters, setFilters] = useState<FilterState>({
    status: "all",
    group: "all"
  });
  const [insuranceFilter, setInsuranceFilter] = useState<string[]>([]);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Selection state
  const [selectedPatients, setSelectedPatients] = useState<Set<string>>(new Set());

  // Modal states
  const [addPatientModalOpen, setAddPatientModalOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<OmniPatient | undefined>(undefined);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionMenuPatient, setActionMenuPatient] = useState<OmniPatient | null>(null);
  const [archivedPatientsModalOpen, setArchivedPatientsModalOpen] = useState(false);
  const [groupsMenuAnchor, setGroupsMenuAnchor] = useState<null | HTMLElement>(null);
  const [addGroupDialogOpen, setAddGroupDialogOpen] = useState(false);
  const [editGroupDialogOpen, setEditGroupDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<PatientGroup | null>(null);
  const [groupName, setGroupName] = useState("");
  const [selectedGroupPatients, setSelectedGroupPatients] = useState<string[]>([]);
  const [loadingGroupAction, setLoadingGroupAction] = useState(false);
  const [addToGroupDialogOpen, setAddToGroupDialogOpen] = useState(false);
  const [selectedGroupForPatient, setSelectedGroupForPatient] = useState<string>("");
  const [bulkGroupDialogOpen, setBulkGroupDialogOpen] = useState(false);
  const [selectedBulkGroup, setSelectedBulkGroup] = useState<string>("");

  // Plans of care state
  const [plansOfCareCounts, setPlansOfCareCounts] = useState<Record<string, number>>({});
  const [plansOfCareData, setPlansOfCareData] = useState<Record<string, ActivePlanOfCare[]>>({});
  const [loadingPlansOfCare, setLoadingPlansOfCare] = useState<Record<string, boolean>>({});

  // Auto-add queue state
  const [queue, setQueue] = useState<QueuePatient[]>([]);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success"
  });

  // Get all patients from Redux
  const allPatients = useSelector((state: RootState) => state.patients.patients);
  const groups = organization?.patientGroups || [];
  const hasWebptIntegration = organization?.integrations.includes(EHR.webpt);
  const insurances = useSelector((state: RootState) => state.insurances.insurances);

  const insuranceFilterOptions = useMemo<InsuranceFilterOption[]>(() => {
    const sorted = [...insurances].sort((a, b) => a.name.localeCompare(b.name));
    return [
      { label: "No insurance", value: NO_INSURANCE_VALUE },
      ...sorted.map(ins => ({ label: ins.name, value: ins.insuranceId }))
    ];
  }, [insurances]);

  const handleInsuranceFilterChange = useCallback((next: string[]) => {
    setInsuranceFilter(next);
    setPage(0);
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(0); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load all patients when a filter or search is applied in paginated mode
  useEffect(() => {
    const isFiltered = debouncedSearch !== "" || filters.status !== "all" || filters.group !== "all" || insuranceFilter.length > 0;
    if (isFiltered && !allPatientsLoaded && user?.token) {
      dispatch(getWaitlistPatients({ token: user.token }));
      setPage(0);
      setCursors([undefined]);
    }
  }, [debouncedSearch, filters.status, filters.group, insuranceFilter, allPatientsLoaded, user?.token, dispatch]);

  // Fetch patients on mount
  useEffect(() => {
    if (user?.token) {
      dispatch(getWaitlistPatients({ token: user.token, pageSize: rowsPerPage, sortOrder: serverSortOrder }));
    }
  }, [user?.token, dispatch]);

  // Fetch insurances on mount
  useEffect(() => {
    if (user?.token) {
      dispatch(fetchInsurances(user.token));
    }
  }, [user?.token, dispatch]);

  // Fetch auto-add queue
  useEffect(() => {
    const fetchQueue = async () => {
      if (!user?.token) {
        setQueue([]);
        return;
      }
      try {
        const response = await api.get(`/patients/queue`, user.token);
        setQueue(response.data || []);
      } catch (error) {
        console.error("Error fetching queue:", error);
        setQueue([]);
      }
    };
    fetchQueue();
  }, [user?.token]);

  // Filter and sort patients
  const filteredAndSortedPatients = useMemo(() => {
    let result = [...allPatients];

    // Apply status filter
    if (filters.status === "waitlist") {
      result = result.filter(p => p.waitlist.availableDates.length > 0);
    } else if (filters.status === "no-waitlist") {
      result = result.filter(p => p.waitlist.availableDates.length === 0);
    }

    // Apply group filter
    if (filters.group !== "all") {
      result = result.filter(p => p.waitlist.patientGroupId === filters.group);
    }

    // Apply insurance filter (multi-select; empty = no filter)
    if (insuranceFilter.length > 0) {
      const selected = new Set(insuranceFilter);
      const includeNone = selected.has(NO_INSURANCE_VALUE);
      result = result.filter(p => {
        const id = p.patient.insuranceId;
        if (!id) return includeNone;
        return selected.has(id);
      });
    }

    // Apply search filter
    if (debouncedSearch) {
      const query = debouncedSearch.toLowerCase().replace(/[^a-z0-9]/g, "");
      result = result.filter(p => {
        const ehrId = p[EHR.webpt]?.webptPatientId || "";
        const searchString = `${p.patient.firstName}${p.patient.lastName}${p.patient.phoneNumber}${ehrId}`
          .toLowerCase()
          .replace(/[^a-z0-9]/g, "");
        return searchString.includes(query);
      });
    }

    // Apply sorting
    result.sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case "name":
          comparison = `${a.patient.firstName} ${a.patient.lastName}`.localeCompare(
            `${b.patient.firstName} ${b.patient.lastName}`
          );
          break;
        case "phone":
          comparison = a.patient.phoneNumber.localeCompare(b.patient.phoneNumber);
          break;
        case "waitlist":
          comparison = countAvailableDays(filterPastAvailableDates(a.waitlist.availableDates)) - countAvailableDays(filterPastAvailableDates(b.waitlist.availableDates));
          break;
        case "group":
          const groupA = groups.find(g => g.patientGroupId === a.waitlist.patientGroupId)?.name || "";
          const groupB = groups.find(g => g.patientGroupId === b.waitlist.patientGroupId)?.name || "";
          comparison = groupA.localeCompare(groupB);
          break;
        case "priority":
          comparison = (a.waitlist.priority || 0) - (b.waitlist.priority || 0);
          break;
        case "createdAt":
          const dateA = new Date(a.patient.createdAt || 0).getTime();
          const dateB = new Date(b.patient.createdAt || 0).getTime();
          comparison = dateA - dateB;
          break;
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });

    return result;
  }, [allPatients, filters, insuranceFilter, debouncedSearch, sortField, sortOrder, groups]);

  // Paginated patients — server already paged when allPatientsLoaded is false
  const paginatedPatients = useMemo(() => {
    if (!allPatientsLoaded) return filteredAndSortedPatients;
    const start = page * rowsPerPage;
    return filteredAndSortedPatients.slice(start, start + rowsPerPage);
  }, [filteredAndSortedPatients, page, rowsPerPage, allPatientsLoaded]);

  // Fetch plans of care for visible patients
  useEffect(() => {
    if (!hasWebptIntegration || !user?.token) return;

    const fetchPlansOfCare = async () => {
      const patientIds = paginatedPatients.map(p => p.patientId);
      
      // Only fetch for patients we haven't loaded yet
      const toFetch = patientIds.filter(id => 
        plansOfCareCounts[id] === undefined && !loadingPlansOfCare[id]
      );

      if (toFetch.length === 0) return;

      // Mark as loading
      const loadingState: Record<string, boolean> = {};
      toFetch.forEach(id => loadingState[id] = true);
      setLoadingPlansOfCare(prev => ({ ...prev, ...loadingState }));

      // Fetch in parallel
      const results = await Promise.allSettled(
        toFetch.map(async (patientId) => {
          try {
            const response = await api.get(
              `/plan-of-care/patient/${patientId}/brief`,
              user.token!
            );
            return { patientId, plans: response.data || [], count: response.data?.length || 0 };
          } catch (error) {
            return { patientId, plans: [], count: 0 };
          }
        })
      );

      // Update counts and data
      const counts: Record<string, number> = {};
      const plansData: Record<string, ActivePlanOfCare[]> = {};
      const loading: Record<string, boolean> = {};
      results.forEach((result) => {
        if (result.status === "fulfilled") {
          counts[result.value.patientId] = result.value.count;
          plansData[result.value.patientId] = result.value.plans;
          loading[result.value.patientId] = false;
        }
      });

      setPlansOfCareCounts(prev => ({ ...prev, ...counts }));
      setPlansOfCareData(prev => ({ ...prev, ...plansData }));
      setLoadingPlansOfCare(prev => ({ ...prev, ...loading }));
    };

    fetchPlansOfCare();
  }, [paginatedPatients, hasWebptIntegration, user?.token]);

  // Handlers
  const handleSort = (field: SortField) => {
    const newOrder = sortField === field ? (sortOrder === "asc" ? "desc" : "asc") : "asc";
    if (sortField !== field) setSortField(field);
    setSortOrder(newOrder);

    if (field === "createdAt" && !allPatientsLoaded && user?.token && newOrder !== serverSortOrder) {
      setServerSortOrder(newOrder);
      dispatch(clearPageCache());
      setCursors([undefined]);
      setPage(0);
      dispatch(getWaitlistPatients({ token: user.token, pageSize: rowsPerPage, sortOrder: newOrder }));
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    if (loadingPatients && !allPatientsLoaded) return;
    if (!allPatientsLoaded && user?.token) {
      let cursor: string | undefined;
      if (newPage === 0) {
        cursor = undefined;
      } else if (newPage > page) {
        cursor = nextCursor;
        setCursors(prev => { const updated = [...prev]; updated[newPage] = cursor; return updated; });
      } else {
        cursor = cursors[newPage];
      }
      const cacheKey = cursor ?? "start";
      if (pageCache[cacheKey]) {
        dispatch(restorePageFromCache(cursor));
      } else {
        dispatch(getWaitlistPatients({ token: user.token, cursor, pageSize: rowsPerPage, sortOrder: serverSortOrder }));
      }
    }
    setPage(newPage);
    setSelectedPatients(new Set());
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
    setCursors([undefined]);
    dispatch(clearPageCache());
    if (!allPatientsLoaded && user?.token) {
      dispatch(getWaitlistPatients({ token: user.token, pageSize: newSize, sortOrder: serverSortOrder }));
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = new Set(paginatedPatients.map(p => p.patientId));
      setSelectedPatients(newSelected);
    } else {
      setSelectedPatients(new Set());
    }
  };

  const handleSelectOne = (patientId: string) => {
    const newSelected = new Set(selectedPatients);
    if (newSelected.has(patientId)) {
      newSelected.delete(patientId);
    } else {
      newSelected.add(patientId);
    }
    setSelectedPatients(newSelected);
  };

  const handleOpenActionMenu = (event: React.MouseEvent<HTMLElement>, patient: OmniPatient) => {
    event.preventDefault();
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setActionMenuPatient(patient);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuPatient(null);
  };

  const handleArchivePatient = async (patientId: string) => {
    if (!user?.token) return;
    handleCloseActionMenu();
    await dispatch(archivePatient({ token: user.token, patientId }));
    setSnackbar({ open: true, message: "Patient archived", severity: "success" });
  };

  const handleUnarchivePatient = async (patientId: string) => {
    if (!user?.token) return;
    handleCloseActionMenu();
    await dispatch(unarchivePatient({ token: user.token, patientId }));
    setSnackbar({ open: true, message: "Patient unarchived", severity: "success" });
  };

  const handleUpdatePatient = (patient: OmniPatient) => {
    setSelectedPatient(patient);
    setUpdateOpen(true);
    handleCloseActionMenu();
  };

  const handleBulkArchive = async () => {
    if (!user?.token || selectedPatients.size === 0) return;

    await Promise.all(
      Array.from(selectedPatients).map(patientId =>
        dispatch(archivePatient({ token: user.token!, patientId }))
      )
    );

    setSelectedPatients(new Set());
    setSnackbar({
      open: true,
      message: `${selectedPatients.size} patient(s) archived`,
      severity: "success"
    });
  };

  const handleBulkAddToGroup = async () => {
    if (!user?.token || selectedPatients.size === 0 || !selectedBulkGroup) return;

    setLoadingGroupAction(true);
    try {
      const selectedIds = Array.from(selectedPatients);
      const totalSelected = selectedIds.length;

      const currentGroupIds = new Set<string>();
      allPatients.forEach(p => {
        if (selectedPatients.has(p.patientId) && p.waitlist.patientGroupId) {
          currentGroupIds.add(p.waitlist.patientGroupId);
        }
      });

      if (selectedBulkGroup === "none") {
        for (const groupId of currentGroupIds) {
          const group = groups.find(g => g.patientGroupId === groupId);
          if (!group) continue;
          const remainingPatients = allPatients
            .filter(p => p.waitlist.patientGroupId === groupId && !selectedPatients.has(p.patientId))
            .map(p => p.patientId);
          await api.patch(
            `/patients/groups/${groupId}`,
            user.token,
            { name: group.name, patientIds: remainingPatients }
          );
        }

        refetchPatients(true);
        await dispatch(getSession());
        setBulkGroupDialogOpen(false);
        setSelectedBulkGroup("");
        setSelectedPatients(new Set());
        setSnackbar({
          open: true,
          message: `${totalSelected} patient(s) removed from group`,
          severity: "success"
        });
        return;
      }

      const targetGroup = groups.find(g => g.patientGroupId === selectedBulkGroup);
      if (!targetGroup) return;

      for (const groupId of currentGroupIds) {
        if (groupId === selectedBulkGroup) continue;
        const group = groups.find(g => g.patientGroupId === groupId);
        if (!group) continue;
        const remainingPatients = allPatients
          .filter(p => p.waitlist.patientGroupId === groupId && !selectedPatients.has(p.patientId))
          .map(p => p.patientId);
        await api.patch(
          `/patients/groups/${groupId}`,
          user.token,
          { name: group.name, patientIds: remainingPatients }
        );
      }

      const existingTargetPatients = allPatients
        .filter(p => p.waitlist.patientGroupId === selectedBulkGroup)
        .map(p => p.patientId);
      const updatedTargetPatients = Array.from(new Set([...existingTargetPatients, ...selectedIds]));

      await api.patch(
        `/patients/groups/${selectedBulkGroup}`,
        user.token,
        { name: targetGroup.name, patientIds: updatedTargetPatients }
      );

      refetchPatients(true);
      await dispatch(getSession());
      setBulkGroupDialogOpen(false);
      setSelectedBulkGroup("");
      setSelectedPatients(new Set());
      setSnackbar({
        open: true,
        message: `${totalSelected} patient(s) moved to ${targetGroup.name}`,
        severity: "success"
      });
    } catch (error) {
      console.error("Error moving patients to group:", error);
      setSnackbar({ open: true, message: "Failed to move patients to group", severity: "error" });
    } finally {
      setLoadingGroupAction(false);
    }
  };

  // Group management handlers
  const handleCreateGroup = async () => {
    if (!user?.token || !groupName.trim()) return;
    
    setLoadingGroupAction(true);
    try {
      await api.post("/patients/groups", user.token, {
        name: groupName,
        patientIds: selectedGroupPatients
      });
      refetchPatients(true);
      await dispatch(getSession());
      setAddGroupDialogOpen(false);
      setGroupName("");
      setSelectedGroupPatients([]);
      setSnackbar({ open: true, message: "Group created successfully", severity: "success" });
    } catch (error) {
      console.error("Error creating group:", error);
      setSnackbar({ open: true, message: "Failed to create group", severity: "error" });
    } finally {
      setLoadingGroupAction(false);
    }
  };

  const handleUpdateGroup = async () => {
    if (!user?.token || !selectedGroup || !groupName.trim()) return;
    
    setLoadingGroupAction(true);
    try {
      await api.patch(
        `/patients/groups/${selectedGroup.patientGroupId}`,
        user.token,
        {
          name: groupName,
          patientIds: selectedGroupPatients
        }
      );
      refetchPatients(true);
      await dispatch(getSession());
      setEditGroupDialogOpen(false);
      setSelectedGroup(null);
      setGroupName("");
      setSelectedGroupPatients([]);
      setSnackbar({ open: true, message: "Group updated successfully", severity: "success" });
    } catch (error) {
      console.error("Error updating group:", error);
      setSnackbar({ open: true, message: "Failed to update group", severity: "error" });
    } finally {
      setLoadingGroupAction(false);
    }
  };

  const handleDeleteGroup = async (group: PatientGroup) => {
    if (!user?.token) return;
    
    // Check if group has patients
    const patientsInGroup = allPatients.filter(
      p => p.waitlist.patientGroupId === group.patientGroupId
    ).length;
    
    if (patientsInGroup > 0) {
      setSnackbar({ 
        open: true, 
        message: "Cannot delete group with patients. Remove patients first.", 
        severity: "error" 
      });
      return;
    }
    
    try {
      await api.delete(`/patients/groups/${group.patientGroupId}`, user.token);
      refetchPatients(true);
      await dispatch(getSession());
      setSnackbar({ open: true, message: "Group deleted successfully", severity: "success" });
    } catch (error) {
      console.error("Error deleting group:", error);
      setSnackbar({ open: true, message: "Failed to delete group", severity: "error" });
    }
  };

  const handleOpenEditGroup = (group: PatientGroup) => {
    setSelectedGroup(group);
    setGroupName(group.name);
    const patientsInGroup = allPatients
      .filter(p => p.waitlist.patientGroupId === group.patientGroupId)
      .map(p => p.patientId);
    setSelectedGroupPatients(patientsInGroup);
    setEditGroupDialogOpen(true);
    setGroupsMenuAnchor(null);
  };

  const handleGroupDragEnd = async (result: DropResult) => {
    if (!result.destination || !user?.token) return;
    if (result.source.index === result.destination.index) return;

    const items = Array.from(groups);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Optimistically update UI
    dispatch(
      setOrganizationAttribute({
        attribute: "patientGroups",
        value: items
      })
    );

    try {
      // Update backend
      await dispatch(
        updateOrganizationAttribute({
          token: user.token,
          attribute: "patientGroupIds",
          value: items.map((group) => group.patientGroupId)
        })
      );
      
      setSnackbar({
        open: true,
        message: "Group priority updated",
        severity: "success"
      });
    } catch (error) {
      console.error("Error updating group order:", error);
      setSnackbar({
        open: true,
        message: "Failed to update group priority",
        severity: "error"
      });
      // Revert on error
      await dispatch(getSession());
    }
  };

  const handleAddPatientToGroup = async () => {
    if (!user?.token || !actionMenuPatient) return;
    
    setLoadingGroupAction(true);
    try {
      const currentGroupId = actionMenuPatient.waitlist.patientGroupId;
      
      // If "none" is selected, remove patient from their current group
      if (selectedGroupForPatient === "none") {
        if (currentGroupId) {
          const currentGroup = groups.find(g => g.patientGroupId === currentGroupId);
          if (currentGroup) {
            // Get current patients in the group excluding this patient
            const updatedPatients = allPatients
              .filter(p => p.waitlist.patientGroupId === currentGroupId && p.patientId !== actionMenuPatient.patientId)
              .map(p => p.patientId);

            await api.patch(
              `/patients/groups/${currentGroupId}`,
              user.token,
              {
                name: currentGroup.name,
                patientIds: updatedPatients
              }
            );
          }
        }
        
        refetchPatients(true);
        await dispatch(getSession());
        setAddToGroupDialogOpen(false);
        setSelectedGroupForPatient("");
        setActionMenuPatient(null);
        setSnackbar({ open: true, message: "Patient removed from group", severity: "success" });
        return;
      }

      // Handle adding/changing to a new group
      if (!selectedGroupForPatient) return;
      
      const targetGroup = groups.find(g => g.patientGroupId === selectedGroupForPatient);
      if (!targetGroup) return;

      // Remove from current group if exists
      if (currentGroupId && currentGroupId !== selectedGroupForPatient) {
        const currentGroup = groups.find(g => g.patientGroupId === currentGroupId);
        if (currentGroup) {
          const currentGroupPatients = allPatients
            .filter(p => p.waitlist.patientGroupId === currentGroupId && p.patientId !== actionMenuPatient.patientId)
            .map(p => p.patientId);

          await api.patch(
            `/patients/groups/${currentGroupId}`,
            user.token,
            {
              name: currentGroup.name,
              patientIds: currentGroupPatients
            }
          );
        }
      }

      // Add to new group
      const targetGroupPatients = allPatients
        .filter(p => p.waitlist.patientGroupId === selectedGroupForPatient)
        .map(p => p.patientId);
      
      const updatedPatients = targetGroupPatients.includes(actionMenuPatient.patientId)
        ? targetGroupPatients
        : [...targetGroupPatients, actionMenuPatient.patientId];

      await api.patch(
        `/patients/groups/${selectedGroupForPatient}`,
        user.token,
        {
          name: targetGroup.name,
          patientIds: updatedPatients
        }
      );

      refetchPatients(true);
      await dispatch(getSession());
      setAddToGroupDialogOpen(false);
      setSelectedGroupForPatient("");
      setActionMenuPatient(null);
      
      const message = currentGroupId 
        ? "Patient group changed successfully" 
        : "Patient added to group successfully";
      setSnackbar({ open: true, message, severity: "success" });
    } catch (error) {
      console.error("Error updating patient group:", error);
      setSnackbar({ open: true, message: "Failed to update patient group", severity: "error" });
    } finally {
      setLoadingGroupAction(false);
    }
  };

  const getGroupName = (patientGroupId?: string | null) => {
    if (!patientGroupId) return "-";
    return groups.find(g => g.patientGroupId === patientGroupId)?.name || "-";
  };

  const getInsuranceName = (insuranceId?: string) => {
    if (!insuranceId) return "-";
    return insurances.find(i => i.insuranceId === insuranceId)?.name || "-";
  };

  const getIgnoreAuthCount = (patientId: string): number => {
    const plans = plansOfCareData[patientId] || [];
    return plans.filter(plan => plan.ignoreAuthorization === true).length;
  };

  const insuranceBypassesAuth = (insuranceId?: string): boolean => {
    if (!insuranceId) return false;
    const insurance = insurances.find(i => i.insuranceId === insuranceId);
    return insurance?.byPassAuthorization === true;
  };

  const isPatientRevoked = (patient: OmniPatient) => {
    const consentHistory = patient.patient.consentHistory;
    const latestConsent = consentHistory[consentHistory.length - 1];
    return latestConsent.consent === Consent.revoked;
  };

  // Check if patient was auto-added
  const isAutoAdded = useMemo(() => {
    const autoAddedMap: Record<string, boolean> = {};
    queue.forEach((item) => {
      if (item.done && item.state === QueuePatientState.added && !item.error) {
        autoAddedMap[String(item.webptPatientId)] = true;
      }
    });
    return autoAddedMap;
  }, [queue]);

  const refetchPatients = useCallback((quietly = true) => {
    if (!user?.token) return;
    if (allPatientsLoaded) {
      dispatch(getWaitlistPatients({ token: user.token, quietly }));
    } else {
      dispatch(getWaitlistPatients({ token: user.token, quietly, cursor: currentCursor, pageSize: rowsPerPage, sortOrder: serverSortOrder }));
    }
  }, [user?.token, allPatientsLoaded, currentCursor, rowsPerPage, serverSortOrder, dispatch]);

  const handleLoadAll = useCallback(() => {
    if (!user?.token) return;
    dispatch(getWaitlistPatients({ token: user.token }));
    setPage(0);
    setCursors([undefined]);
  }, [user?.token, dispatch]);

  const getIsAutoAdded = (patient: OmniPatient): boolean => {
    const ehrId = patient[EHR.webpt]?.webptPatientId;
    if (!ehrId) return false;
    return isAutoAdded[String(ehrId)] || false;
  };

  // Active filters count
  const activeFiltersCount =
    (filters.status !== "all" ? 1 : 0) +
    (filters.group !== "all" ? 1 : 0) +
    (insuranceFilter.length > 0 ? 1 : 0) +
    (includeArchivedPatients ? 1 : 0);

  return (
    <Box
      sx={{
        marginTop: { xs: "75px", sm: 0 },
        color: "#FFF",
        minHeight: "100vh",
        pb: 4
      }}
    >
      <SignoutHeader />

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="700" sx={{ mb: 0.5 }}>
          Patients
        </Typography>
        <Typography variant="body1" sx={{ color: Colors.info }}>
          Manage patients, waitlist, and plan of care.
        </Typography>
      </Box>

      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          gap: 2,
          flexWrap: "wrap"
        }}
      >
        {/* Left side - Search and Filters */}
        <Box sx={{ display: "flex", gap: 2, flex: 1, minWidth: "300px" }}>
          <TextField
            placeholder="Search patients by name, ID, or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: Colors.info, fontSize: "18px" }} />
                </InputAdornment>
              )
            }}
            sx={{
              flex: 1,
              maxWidth: "400px",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "rgba(255, 255, 255, 0.02)",
                color: "#FFF",
                fontSize: "0.875rem",
                "& fieldset": {
                  borderColor: "rgba(255, 255, 255, 0.12)"
                },
                "&:hover fieldset": {
                  borderColor: "rgba(255, 255, 255, 0.2)"
                },
                "&.Mui-focused fieldset": {
                  borderColor: Colors.primary,
                  borderWidth: "1px"
                }
              }
            }}
          />

          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
            sx={{
              textTransform: "none",
              fontSize: "0.875rem",
              borderColor: activeFiltersCount > 0 ? Colors.primary : "rgba(255, 255, 255, 0.2)",
              color: activeFiltersCount > 0 ? Colors.primary : "#FFF",
              "&:hover": {
                borderColor: Colors.primary,
                backgroundColor: "rgba(25, 118, 210, 0.08)"
              }
            }}
          >
            Filters {activeFiltersCount > 0 && `(${activeFiltersCount})`}
          </Button>

          <Menu
            anchorEl={filterMenuAnchor}
            open={Boolean(filterMenuAnchor)}
            onClose={() => setFilterMenuAnchor(null)}
            PaperProps={{
              sx: {
                backgroundColor: Colors.bg3,
                color: "#FFF",
                minWidth: "200px",
                mt: 1
              }
            }}
          >
            <MenuItem disabled sx={{ opacity: 0.5, fontSize: "0.75rem", fontWeight: 600 }}>
              STATUS
            </MenuItem>
            <MenuItem 
              onClick={() => {
                setFilters(prev => ({ ...prev, status: "all" }));
                setPage(0);
              }}
              selected={filters.status === "all"}
            >
              All Patients
            </MenuItem>
            <MenuItem 
              onClick={() => {
                setFilters(prev => ({ ...prev, status: "waitlist" }));
                setPage(0);
              }}
              selected={filters.status === "waitlist"}
            >
              On Waitlist
            </MenuItem>
            <MenuItem 
              onClick={() => {
                setFilters(prev => ({ ...prev, status: "no-waitlist" }));
                setPage(0);
              }}
              selected={filters.status === "no-waitlist"}
            >
              Not on Waitlist
            </MenuItem>
            
            <Box sx={{ borderTop: "1px solid rgba(255, 255, 255, 0.08)", my: 1 }} />
            
            <MenuItem disabled sx={{ opacity: 0.5, fontSize: "0.75rem", fontWeight: 600 }}>
              GROUP
            </MenuItem>
            <MenuItem 
              onClick={() => {
                setFilters(prev => ({ ...prev, group: "all" }));
                setPage(0);
              }}
              selected={filters.group === "all"}
            >
              All Groups
            </MenuItem>
            {groups.map(group => (
              <MenuItem
                key={group.patientGroupId}
                onClick={() => {
                  setFilters(prev => ({ ...prev, group: group.patientGroupId }));
                  setPage(0);
                }}
                selected={filters.group === group.patientGroupId}
              >
                {group.name}
              </MenuItem>
            ))}

            <Box sx={{ borderTop: "1px solid rgba(255, 255, 255, 0.08)", my: 1 }} />

            <MenuItem
              onClick={async () => {
                if (!user?.token) return;
                const newValue = !includeArchivedPatients;
                dispatch(setIncludeArchivedPatients(newValue));
                dispatch(clearPageCache());
                if (allPatientsLoaded) {
                  await dispatch(getWaitlistPatients({ token: user.token }));
                } else {
                  await dispatch(getWaitlistPatients({ token: user.token, pageSize: rowsPerPage, sortOrder: serverSortOrder }));
                }
                setPage(0);
                setCursors([undefined]);
              }}
            >
              <Checkbox 
                checked={includeArchivedPatients} 
                size="small"
                sx={{ 
                  color: Colors.info,
                  "&.Mui-checked": { color: Colors.primary }
                }}
              />
              Show Archived
            </MenuItem>
          </Menu>

          <InsuranceFilterPicker
            options={insuranceFilterOptions}
            selected={insuranceFilter}
            onChange={handleInsuranceFilterChange}
          />
        </Box>

        {/* Right side - Actions */}
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          {selectedPatients.size > 0 && (
            <>
              <Typography variant="body2" sx={{ color: Colors.info, fontSize: "0.875rem" }}>
                {selectedPatients.size} selected
              </Typography>
              {groups.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<GroupIcon />}
                  onClick={() => {
                    setSelectedBulkGroup("");
                    setBulkGroupDialogOpen(true);
                  }}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.875rem",
                    borderColor: "rgba(255, 255, 255, 0.2)",
                    color: "#FFF"
                  }}
                >
                  Move to Group
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                startIcon={<ArchiveIcon />}
                onClick={handleBulkArchive}
                sx={{
                  textTransform: "none",
                  fontSize: "0.875rem",
                  borderColor: "rgba(255, 255, 255, 0.2)",
                  color: "#FFF"
                }}
              >
                Archive
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => setSelectedPatients(new Set())}
                sx={{
                  textTransform: "none",
                  fontSize: "0.875rem",
                  color: Colors.info
                }}
              >
                Clear
              </Button>
            </>
          )}

          <Button
            variant="outlined"
            startIcon={<GroupIcon />}
            onClick={(e) => {
              const anchor = e.currentTarget;
              setGroupsMenuAnchor(anchor);
              // Group sizes are computed from allPatients, which may only hold the current page —
              // load the full patient list first so counts aren't understated.
              if (!allPatientsLoaded && user?.token) {
                dispatch(getWaitlistPatients({ token: user.token }));
              }
            }}
            sx={{
              textTransform: "none",
              fontSize: "0.875rem",
              borderColor: "rgba(255, 255, 255, 0.2)",
              color: "#FFF",
              "&:hover": {
                borderColor: Colors.primary,
                backgroundColor: "rgba(25, 118, 210, 0.08)"
              }
            }}
          >
            Groups ({groups.length})
          </Button>

          <Button
            variant="outlined"
            startIcon={<ArchiveIcon />}
            onClick={() => setArchivedPatientsModalOpen(true)}
            sx={{
              textTransform: "none",
              fontSize: "0.875rem",
              borderColor: "rgba(255, 255, 255, 0.2)",
              color: "#FFF",
              "&:hover": {
                borderColor: Colors.primary,
                backgroundColor: "rgba(25, 118, 210, 0.08)"
              }
            }}
          >
            Archived
          </Button>

          <Button
            variant="outlined"
            startIcon={<AutoAwesomeIcon />}
            component={RouterLink}
            to="/dashboard/patients/auto-add"
            sx={{
              textTransform: "none",
              fontSize: "0.875rem",
              borderColor: "rgba(255, 255, 255, 0.2)",
              color: "#FFF",
              "&:hover": {
                borderColor: Colors.primary,
                backgroundColor: "rgba(25, 118, 210, 0.08)"
              }
            }}
          >
            Recommended Patients
          </Button>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddPatientModalOpen(true)}
            sx={{
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none"
              }
            }}
          >
            Add Patient
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          backgroundColor: "rgba(255, 255, 255, 0.02)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          borderRadius: "12px",
          boxShadow: "none"
        }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{
                "& th": {
                  borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
                  color: Colors.info,
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  py: 2
                }
              }}
            >
              <TableCell padding="checkbox">
                <Checkbox
                  checked={paginatedPatients.length > 0 && selectedPatients.size === paginatedPatients.length}
                  indeterminate={selectedPatients.size > 0 && selectedPatients.size < paginatedPatients.length}
                  onChange={handleSelectAll}
                  size="small"
                  sx={{
                    color: Colors.info,
                    "&.Mui-checked": { color: Colors.primary },
                    "&.MuiCheckbox-indeterminate": { color: Colors.primary }
                  }}
                />
              </TableCell>
              <TableCell>
                Patient
              </TableCell>
              <TableCell>
                Phone
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === "createdAt"}
                  direction={sortField === "createdAt" ? sortOrder : "asc"}
                  onClick={() => handleSort("createdAt")}
                  sx={{
                    color: Colors.info,
                    "&.Mui-active": { color: "#FFF" },
                    "& .MuiTableSortLabel-icon": { color: "#FFF !important" }
                  }}
                >
                  Created On
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ color: Colors.info, fontSize: "0.75rem", fontWeight: 600 }}>
                Created By
              </TableCell>
              <TableCell>
                Group
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === "waitlist"}
                  direction={sortField === "waitlist" ? sortOrder : "asc"}
                  onClick={() => handleSort("waitlist")}
                  sx={{
                    color: Colors.info,
                    "&.Mui-active": { color: "#FFF" },
                    "& .MuiTableSortLabel-icon": { color: "#FFF !important" }
                  }}
                >
                  Waitlist
                </TableSortLabel>
              </TableCell>
              {hasWebptIntegration && (
                <>
                  <TableCell sx={{ color: Colors.info, fontSize: "0.75rem", fontWeight: 600 }}>
                    Plans of Care
                  </TableCell>
                  <TableCell sx={{ color: Colors.info, fontSize: "0.75rem", fontWeight: 600 }}>
                    Ignore Visit Restrictions
                  </TableCell>
                </>
              )}
              <TableCell sx={{ color: Colors.info, fontSize: "0.75rem", fontWeight: 600 }}>
                Insurance
              </TableCell>
              <TableCell sx={{ color: Colors.info, fontSize: "0.75rem", fontWeight: 600 }}>
                Bypass Auth
              </TableCell>
              <TableCell 
                align="right"
                sx={{
                  position: "sticky",
                  right: 0,
                  zIndex: 10
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingPatients ? (
              // Skeleton loading
              Array.from({ length: rowsPerPage }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell padding="checkbox">
                    <Skeleton variant="rectangular" width={18} height={18} sx={{ bgcolor: "rgba(255, 255, 255, 0.08)" }} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" sx={{ bgcolor: "rgba(255, 255, 255, 0.08)" }} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" sx={{ bgcolor: "rgba(255, 255, 255, 0.08)" }} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" sx={{ bgcolor: "rgba(255, 255, 255, 0.08)" }} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" sx={{ bgcolor: "rgba(255, 255, 255, 0.08)" }} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" sx={{ bgcolor: "rgba(255, 255, 255, 0.08)" }} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" sx={{ bgcolor: "rgba(255, 255, 255, 0.08)" }} />
                  </TableCell>
                  {hasWebptIntegration && (
                    <>
                      <TableCell>
                        <Skeleton variant="text" sx={{ bgcolor: "rgba(255, 255, 255, 0.08)" }} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" sx={{ bgcolor: "rgba(255, 255, 255, 0.08)" }} />
                      </TableCell>
                    </>
                  )}
                  <TableCell>
                    <Skeleton variant="text" sx={{ bgcolor: "rgba(255, 255, 255, 0.08)" }} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" sx={{ bgcolor: "rgba(255, 255, 255, 0.08)" }} />
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      right: 0,
                      zIndex: 10
                    }}
                  >
                    <Skeleton variant="text" sx={{ bgcolor: "rgba(255, 255, 255, 0.08)" }} />
                  </TableCell>
                </TableRow>
              ))
            ) : paginatedPatients.length === 0 ? (
              // Empty state
              <TableRow>
                <TableCell colSpan={hasWebptIntegration ? 12 : 10} sx={{ border: 0 }}>
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 8,
                      color: Colors.info
                    }}
                  >
                    <SearchIcon sx={{ fontSize: "48px", opacity: 0.2, mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1, fontSize: "0.95rem", fontWeight: 600, color: "#FFF" }}>
                      No patients found
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: "0.813rem" }}>
                      {searchQuery || activeFiltersCount > 0
                        ? "Try adjusting your search or filters"
                        : "Add your first patient to get started"}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              // Patient rows
              paginatedPatients.map((patient) => {
                const isSelected = selectedPatients.has(patient.patientId);
                const isRevoked = isPatientRevoked(patient);
                const isOnWaitlist = patient.waitlist.availableDates.length > 0;
                const plansCount = plansOfCareCounts[patient.patientId];
                const isLoadingPlans = loadingPlansOfCare[patient.patientId];
                const ehrId = patient[EHR.webpt]?.webptPatientId;
                const nameString = patient.patient.preferredName
                  ? `${patient.patient.firstName} "${patient.patient.preferredName}" ${patient.patient.lastName}`
                  : `${patient.patient.firstName} ${patient.patient.lastName}`;

                return (
                  <TableRow
                    key={patient.patientId}
                    hover
                    selected={isSelected}
                    component={RouterLink}
                    to={`/dashboard/patients/${patient.patientId}`}
                    sx={{
                      cursor: "pointer",
                      textDecoration: "none",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.04) !important"
                      },
                      "& td": {
                        borderBottom: "1px solid rgba(255, 255, 255, 0.04)",
                        color: "#FFF",
                        fontSize: "0.875rem",
                        py: 2
                      },
                      "&.Mui-selected": {
                        backgroundColor: "rgba(25, 118, 210, 0.08)"
                      }
                    }}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleSelectOne(patient.patientId)}
                        size="small"
                        sx={{
                          color: Colors.info,
                          "&.Mui-checked": { color: Colors.primary }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                            {nameString}
                          </Typography>
                          {isRevoked && (
                            <Tooltip title="Patient on Do Not Call list" arrow>
                              <WarningIcon sx={{ color: Colors.error, fontSize: "16px" }} />
                            </Tooltip>
                          )}
                          {patient.waitlist.archived && (
                            <Chip
                              label="Archived"
                              size="small"
                              sx={{
                                height: "20px",
                                fontSize: "0.688rem",
                                fontWeight: 500,
                                backgroundColor: "rgba(255, 152, 0, 0.12)",
                                color: "#FF9800",
                                border: "1px solid rgba(255, 152, 0, 0.3)"
                              }}
                            />
                          )}
                        </Box>
                        <Typography variant="caption" sx={{ color: Colors.info, fontSize: "0.75rem" }}>
                          ID: {ehrId}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                        {convertE164ToReadable(patient.patient.phoneNumber)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: "0.875rem", color: Colors.info }}>
                        {patient.patient.createdAt 
                          ? DateTime.fromISO(patient.patient.createdAt).toFormat("MMM dd, yyyy")
                          : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getIsAutoAdded(patient) ? (
                        <Typography variant="body2" sx={{ fontSize: "0.875rem", color: Colors.info }}>
                          Auto-Add
                      </Typography>
                      ) : (
                        <Typography variant="body2" sx={{ fontSize: "0.875rem", color: Colors.info }}>
                          Manual
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: "0.875rem", color: Colors.info }}>
                        {getGroupName(patient.waitlist.patientGroupId)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        {isOnWaitlist ? (
                          <Chip
                            icon={<CheckCircleIcon sx={{ fontSize: "14px !important" }} />}
                            label={`${countAvailableDays(filterPastAvailableDates(patient.waitlist.availableDates))} day${countAvailableDays(filterPastAvailableDates(patient.waitlist.availableDates)) > 1 ? 's' : ''}`}
                            size="small"
                            sx={{
                              height: "24px",
                              fontSize: "0.75rem",
                              backgroundColor: "rgba(76, 175, 80, 0.12)",
                              color: "#4CAF50",
                              border: "1px solid rgba(76, 175, 80, 0.3)",
                              "& .MuiChip-icon": { color: "#4CAF50" }
                            }}
                          />
                        ) : (
                          <Typography variant="body2" sx={{ fontSize: "0.875rem", color: Colors.info }}>
                            -
                          </Typography>
                        )}
                        {patient.patient.waitlistPausedNotifiedAt && !waitlistAgent?.disableInactivityPause && (
                          <Tooltip title="Waitlist texts paused due to prolonged inactivity" arrow>
                            <Chip
                              label="Paused"
                              size="small"
                              sx={{
                                height: "20px",
                                fontSize: "0.688rem",
                                fontWeight: 500,
                                backgroundColor: "rgba(244, 67, 54, 0.12)",
                                color: "#F44336",
                                border: "1px solid rgba(244, 67, 54, 0.3)"
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    {hasWebptIntegration && (
                      <>
                        <TableCell>
                          {isLoadingPlans ? (
                            <CircularProgress size={16} sx={{ color: Colors.info }} />
                          ) : plansCount !== undefined ? (
                            plansCount > 0 ? (
                              <Chip
                                label={`${plansCount} plan${plansCount > 1 ? 's' : ''}`}
                                size="small"
                                sx={{
                                  height: "24px",
                                  fontSize: "0.75rem",
                                  backgroundColor: "rgba(33, 150, 243, 0.12)",
                                  color: "#2196F3",
                                  border: "1px solid rgba(33, 150, 243, 0.3)"
                                }}
                              />
                            ) : (
                              <Typography variant="body2" sx={{ fontSize: "0.875rem", color: Colors.info }}>
                                -
                              </Typography>
                            )
                          ) : (
                            <Typography variant="body2" sx={{ fontSize: "0.875rem", color: Colors.info }}>
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {isLoadingPlans ? (
                            <CircularProgress size={16} sx={{ color: Colors.info }} />
                          ) : plansOfCareData[patient.patientId] !== undefined ? (
                            (() => {
                              const ignoreAuthCount = getIgnoreAuthCount(patient.patientId);
                              return ignoreAuthCount > 0 ? (
                                <Chip
                                  label={`${ignoreAuthCount} plan${ignoreAuthCount > 1 ? 's' : ''}`}
                                  size="small"
                                  sx={{
                                    height: "24px",
                                    fontSize: "0.75rem",
                                    backgroundColor: "rgba(33, 150, 243, 0.12)",
                                    color: "#2196F3",
                                    border: "1px solid rgba(33, 150, 243, 0.3)"
                                  }}
                                />
                              ) : (
                                <Typography variant="body2" sx={{ fontSize: "0.875rem", color: Colors.info }}>
                                  -
                                </Typography>
                              );
                            })()
                          ) : (
                            <Typography variant="body2" sx={{ fontSize: "0.875rem", color: Colors.info }}>
                              -
                            </Typography>
                          )}
                        </TableCell>
                      </>
                    )}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: "0.875rem", color: Colors.info }}>
                        {getInsuranceName(patient.patient.insuranceId)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {insuranceBypassesAuth(patient.patient.insuranceId) ? (
                        <Chip
                          icon={<CheckCircleIcon sx={{ fontSize: "14px !important" }} />}
                          label="Yes"
                          size="small"
                          sx={{
                            height: "24px",
                            fontSize: "0.75rem",
                            backgroundColor: "rgba(76, 175, 80, 0.12)",
                            color: "#4CAF50",
                            border: "1px solid rgba(76, 175, 80, 0.3)",
                            "& .MuiChip-icon": { color: "#4CAF50" }
                          }}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ fontSize: "0.875rem", color: Colors.info }}>
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell 
                      align="right" 
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        position: "sticky",
                        right: 0,
                        zIndex: 10,
                      }}
                    >
                      <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                        <Tooltip title="View Details" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/dashboard/patients/${patient.patientId}`);
                            }}
                            sx={{
                              color: Colors.info,
                              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.08)" }
                            }}
                          >
                            <ArrowForwardIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          size="small"
                          onClick={(e) => handleOpenActionMenu(e, patient)}
                          sx={{
                            color: Colors.info,
                            "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.08)" }
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <Box sx={{ display: "flex", alignItems: "center", borderTop: "1px solid rgba(255, 255, 255, 0.08)" }}>
          {!allPatientsLoaded && (
            <Box sx={{ pl: 2, flexShrink: 0 }}>
              <Button
                size="small"
                variant="text"
                onClick={handleLoadAll}
                sx={{ textTransform: "none", fontSize: "0.8rem", color: Colors.primary }}
              >
                Load all
              </Button>
            </Box>
          )}
          <TablePagination
            component="div"
            count={allPatientsLoaded ? filteredAndSortedPatients.length : (serverTotal ?? 0)}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              flex: 1,
              color: "#FFF",
              "& .MuiTablePagination-select": { color: "#FFF" },
              "& .MuiTablePagination-selectIcon": { color: "#FFF" },
              "& .MuiTablePagination-displayedRows": { color: Colors.info },
              "& .MuiIconButton-root": { color: "#FFF" },
              "& .MuiTablePagination-actions button:last-of-type": loadingPatients && !allPatientsLoaded
                ? { opacity: 0.3, pointerEvents: "none" }
                : {},
            }}
          />
        </Box>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
        PaperProps={{
          sx: {
            backgroundColor: Colors.bg3,
            color: "#FFF",
            minWidth: "200px",
            mt: 1
          }
        }}
      >
        <MenuItem onClick={() => {
          if (actionMenuPatient) {
            setSelectedGroupForPatient(actionMenuPatient.waitlist.patientGroupId || "");
          }
          setAddToGroupDialogOpen(true);
          setActionMenuAnchor(null); // Close menu but keep actionMenuPatient
        }}>
          {actionMenuPatient?.waitlist.patientGroupId ? "Change Group" : "Add to Group"}
        </MenuItem>
        <MenuItem onClick={() => actionMenuPatient && handleUpdatePatient(actionMenuPatient)}>
          Manage Waitlist
        </MenuItem>
        {hasWebptIntegration && (
          <MenuItem onClick={() => {
            if (actionMenuPatient) {
              navigate(`/dashboard/patients/${actionMenuPatient.patientId}/plan-of-care`);
              handleCloseActionMenu();
            }
          }}>
            Create Plan of Care
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          if (actionMenuPatient) {
            navigate(`/dashboard/patients/${actionMenuPatient.patientId}`);
            handleCloseActionMenu();
          }
        }}>
          View Details
        </MenuItem>
        <Box sx={{ borderTop: "1px solid rgba(255, 255, 255, 0.08)", my: 0.5 }} />
        {actionMenuPatient?.waitlist.archived ? (
          <MenuItem 
            onClick={() => actionMenuPatient && handleUnarchivePatient(actionMenuPatient.patientId)}
            sx={{ color: Colors.primary }}
          >
            <UnarchiveIcon fontSize="small" sx={{ mr: 1 }} />
            Unarchive
          </MenuItem>
        ) : (
          <MenuItem 
            onClick={() => actionMenuPatient && handleArchivePatient(actionMenuPatient.patientId)}
            sx={{ color: Colors.error }}
          >
            <ArchiveIcon fontSize="small" sx={{ mr: 1 }} />
            Archive
          </MenuItem>
        )}
      </Menu>

      {/* Groups Dialog */}
      <Dialog
        open={Boolean(groupsMenuAnchor)}
        onClose={() => setGroupsMenuAnchor(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: Colors.bg3,
            color: "#FFF"
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.08)", pb: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Manage Groups
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                setAddGroupDialogOpen(true);
                setGroupsMenuAnchor(null);
              }}
              sx={{
                textTransform: "none",
                fontSize: "0.813rem",
                boxShadow: "none",
                "&:hover": { boxShadow: "none" }
              }}
            >
              Create Group
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, minHeight: "200px", maxHeight: "500px" }}>
          {groups.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6, color: Colors.info }}>
              <GroupIcon sx={{ fontSize: "48px", opacity: 0.2, mb: 2 }} />
              <Typography variant="body2" sx={{ mb: 2 }}>
                No groups yet
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setAddGroupDialogOpen(true);
                  setGroupsMenuAnchor(null);
                }}
                sx={{
                  textTransform: "none",
                  fontSize: "0.875rem",
                  borderColor: "rgba(255, 255, 255, 0.2)",
                  color: "#FFF"
                }}
              >
                Create First Group
              </Button>
            </Box>
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ color: Colors.info, mb: 2, display: "block" }}>
                Drag to reorder priority (higher = more important)
              </Typography>
              <DraggableList
                items={groups}
                onDragEnd={handleGroupDragEnd}
                getItemId={(group) => group.patientGroupId}
                renderItem={(group) => {
                  const groupSizeLoading = !allPatientsLoaded && loadingPatients;
                  const patientsInGroup = allPatients.filter(
                    p => p.waitlist.patientGroupId === group.patientGroupId
                  ).length;

                  return (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 2,
                        mb: 1,
                        backgroundColor: "rgba(255, 255, 255, 0.02)",
                        border: "1px solid rgba(255, 255, 255, 0.08)",
                        borderRadius: "8px",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.04)"
                        }
                      }}
                    >
                      <DragIndicatorIcon
                        sx={{
                          cursor: "grab",
                          color: Colors.info,
                          fontSize: "20px"
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                          {group.name}
                        </Typography>
                        {groupSizeLoading ? (
                          <CircularProgress size={10} sx={{ color: Colors.info, mt: 0.5 }} />
                        ) : (
                          <Typography variant="caption" sx={{ color: Colors.info, fontSize: "0.75rem" }}>
                            {patientsInGroup} patient{patientsInGroup !== 1 ? "s" : ""}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Edit Group" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenEditGroup(group);
                            }}
                            sx={{
                              color: Colors.info,
                              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.08)" }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={groupSizeLoading ? "Loading patient count…" : patientsInGroup > 0 ? "Remove patients first" : "Delete Group"}
                          arrow
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteGroup(group);
                              }}
                              disabled={groupSizeLoading || patientsInGroup > 0}
                              sx={{
                                color: groupSizeLoading || patientsInGroup > 0 ? Colors.info : Colors.error,
                                opacity: groupSizeLoading || patientsInGroup > 0 ? 0.3 : 1,
                                "&:hover": {
                                  backgroundColor: groupSizeLoading || patientsInGroup > 0 ? "transparent" : "rgba(255, 255, 255, 0.08)"
                                }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                }}
                droppableId="groups"
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Group Dialog */}
      <Dialog
        open={addGroupDialogOpen}
        onClose={() => !loadingGroupAction && setAddGroupDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: Colors.bg3,
            color: "#FFF"
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.08)" }}>
          Create New Group
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 3, mt: 3 }}>
            <TextField
              label="Group Name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              fullWidth
              autoFocus
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: "#FFF",
                  "& fieldset": { borderColor: "rgba(255, 255, 255, 0.2)" },
                  "&:hover fieldset": { borderColor: "rgba(255, 255, 255, 0.3)" },
                  "&.Mui-focused fieldset": { borderColor: Colors.primary }
                },
                "& .MuiInputLabel-root": { color: Colors.info },
                "& .MuiInputLabel-root.Mui-focused": { color: Colors.primary }
              }}
            />
          </FormControl>
          
          <CustomAutocomplete
            options={allPatients.map(p => ({
              value: p.patientId,
              label: `${p.patient.firstName} ${p.patient.lastName}`
            }))}
            selectedItems={selectedGroupPatients}
            setSelectedItems={setSelectedGroupPatients}
            label="Select Patients (Optional)"
          />
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid rgba(255, 255, 255, 0.08)", p: 2 }}>
          <Button
            onClick={() => {
              setAddGroupDialogOpen(false);
              setGroupName("");
              setSelectedGroupPatients([]);
            }}
            color="error"
            disabled={loadingGroupAction}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateGroup}
            variant="contained"
            disabled={!groupName.trim() || loadingGroupAction}
            sx={{
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { boxShadow: "none" }
            }}
          >
            {loadingGroupAction ? <CircularProgress size={20} /> : "Create Group"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Group Dialog */}
      <Dialog
        open={editGroupDialogOpen}
        onClose={() => !loadingGroupAction && setEditGroupDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: Colors.bg3,
            color: "#FFF"
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.08)" }}>
          Edit Group
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <FormControl fullWidth sx={{ mb: 3, mt: 3 }}>
            <TextField
              label="Group Name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              fullWidth
              autoFocus
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: "#FFF",
                  "& fieldset": { borderColor: "rgba(255, 255, 255, 0.2)" },
                  "&:hover fieldset": { borderColor: "rgba(255, 255, 255, 0.3)" },
                  "&.Mui-focused fieldset": { borderColor: Colors.primary }
                },
                "& .MuiInputLabel-root": { color: Colors.info },
                "& .MuiInputLabel-root.Mui-focused": { color: Colors.primary }
              }}
            />
          </FormControl>
          
          <CustomAutocomplete
            options={allPatients.map(p => ({
              value: p.patientId,
              label: `${p.patient.firstName} ${p.patient.lastName}`
            }))}
            selectedItems={selectedGroupPatients}
            setSelectedItems={setSelectedGroupPatients}
            label="Select Patients"
          />
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid rgba(255, 255, 255, 0.08)", p: 2 }}>
          <Button
            onClick={() => {
              setEditGroupDialogOpen(false);
              setSelectedGroup(null);
              setGroupName("");
              setSelectedGroupPatients([]);
            }}
            color="error"
            disabled={loadingGroupAction}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateGroup}
            variant="contained"
            disabled={!groupName.trim() || loadingGroupAction}
            sx={{
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { boxShadow: "none" }
            }}
          >
            {loadingGroupAction ? <CircularProgress size={20} /> : "Update Group"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Patient to Group Dialog */}
      <Dialog
        open={addToGroupDialogOpen}
        onClose={() => {
          if (!loadingGroupAction) {
            setAddToGroupDialogOpen(false);
            setActionMenuPatient(null);
            setSelectedGroupForPatient("");
          }
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: Colors.bg3,
            color: "#FFF"
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.08)" }}>
          {actionMenuPatient?.waitlist.patientGroupId ? "Change Group" : "Add Patient to Group"}
        </DialogTitle>
        <DialogContent>
          {actionMenuPatient && (
            <Typography variant="body2" sx={{ mb: 3, mt: 3, color: Colors.info }}>
              Select a group for{" "}
              <strong style={{ color: "#FFF" }}>
                {actionMenuPatient.patient.firstName} {actionMenuPatient.patient.lastName}
              </strong>
            </Typography>
          )}
          
          <FormControl fullWidth sx={{ mt: 3 }}>
            <TextField
              select
              label="Select Group"
              value={selectedGroupForPatient}
              onChange={(e) => setSelectedGroupForPatient(e.target.value)}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: "#FFF",
                  "& fieldset": { borderColor: "rgba(255, 255, 255, 0.2)" },
                  "&:hover fieldset": { borderColor: "rgba(255, 255, 255, 0.3)" },
                  "&.Mui-focused fieldset": { borderColor: Colors.primary }
                },
                "& .MuiInputLabel-root": { color: Colors.info },
                "& .MuiInputLabel-root.Mui-focused": { color: Colors.primary }
              }}
            >
              <MenuItem value="none">
                <em>None</em>
              </MenuItem>
              {groups.length === 0 ? (
                <MenuItem disabled>No groups available</MenuItem>
              ) : (
                groups.map((group) => (
                  <MenuItem key={group.patientGroupId} value={group.patientGroupId}>
                    {group.name}
                  </MenuItem>
                ))
              )}
            </TextField>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid rgba(255, 255, 255, 0.08)", p: 2 }}>
          <Button
            onClick={() => {
              setAddToGroupDialogOpen(false);
              setSelectedGroupForPatient("");
              setActionMenuPatient(null);
            }}
            disabled={loadingGroupAction}
            sx={{ textTransform: "none" }}
            color="error"
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddPatientToGroup}
            variant="contained"
            disabled={!selectedGroupForPatient || loadingGroupAction}
            sx={{
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { boxShadow: "none" }
            }}
          >
            {loadingGroupAction ? <CircularProgress size={20} /> :
              selectedGroupForPatient === "none" ? "Remove from Group" :
              actionMenuPatient?.waitlist.patientGroupId ? "Change Group" : "Add to Group"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Move to Group Dialog */}
      <Dialog
        open={bulkGroupDialogOpen}
        onClose={() => {
          if (!loadingGroupAction) {
            setBulkGroupDialogOpen(false);
            setSelectedBulkGroup("");
          }
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: Colors.bg3,
            color: "#FFF"
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.08)" }}>
          Move to Group
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3, mt: 3, color: Colors.info }}>
            Select a group for{" "}
            <strong style={{ color: "#FFF" }}>
              {selectedPatients.size} selected patient{selectedPatients.size === 1 ? "" : "s"}
            </strong>
          </Typography>

          <FormControl fullWidth sx={{ mt: 1 }}>
            <TextField
              select
              label="Select Group"
              value={selectedBulkGroup}
              onChange={(e) => setSelectedBulkGroup(e.target.value)}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: "#FFF",
                  "& fieldset": { borderColor: "rgba(255, 255, 255, 0.2)" },
                  "&:hover fieldset": { borderColor: "rgba(255, 255, 255, 0.3)" },
                  "&.Mui-focused fieldset": { borderColor: Colors.primary }
                },
                "& .MuiInputLabel-root": { color: Colors.info },
                "& .MuiInputLabel-root.Mui-focused": { color: Colors.primary }
              }}
            >
              <MenuItem value="none">
                <em>None (remove from current group)</em>
              </MenuItem>
              {groups.length === 0 ? (
                <MenuItem disabled>No groups available</MenuItem>
              ) : (
                groups.map((group) => (
                  <MenuItem key={group.patientGroupId} value={group.patientGroupId}>
                    {group.name}
                  </MenuItem>
                ))
              )}
            </TextField>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid rgba(255, 255, 255, 0.08)", p: 2 }}>
          <Button
            onClick={() => {
              setBulkGroupDialogOpen(false);
              setSelectedBulkGroup("");
            }}
            disabled={loadingGroupAction}
            sx={{ textTransform: "none" }}
            color="error"
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkAddToGroup}
            variant="contained"
            disabled={!selectedBulkGroup || loadingGroupAction}
            sx={{
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { boxShadow: "none" }
            }}
          >
            {loadingGroupAction ? <CircularProgress size={20} /> :
              selectedBulkGroup === "none" ? "Remove from Group" : "Move to Group"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modals */}
      <Modal open={addPatientModalOpen} onClose={() => setAddPatientModalOpen(false)}>
        <Sidebar className={addPatientModalOpen ? "open" : ""}>
          {organization?.integrations.includes(EHR.webpt) && (
            <WebptAddPatientModal
              onClick={() => {}}
              onClose={() => setAddPatientModalOpen(false)}
            />
          )}
        </Sidebar>
      </Modal>

      <Modal open={archivedPatientsModalOpen} onClose={() => setArchivedPatientsModalOpen(false)}>
        <Sidebar className={archivedPatientsModalOpen ? "open" : ""}>
          <ArchivedPatientsModal onClose={() => setArchivedPatientsModalOpen(false)} />
        </Sidebar>
      </Modal>

      {selectedPatient && (
        <Sidebar className={updateOpen ? "open" : ""}>
          {organization?.integrations.includes(EHR.webpt) && (
            <UpdateWebptPatientModal
              key={selectedPatient.patientId}
              open={updateOpen}
              onClose={() => setUpdateOpen(false)}
              patient={selectedPatient}
            />
          )}
        </Sidebar>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            backgroundColor: snackbar.severity === "success" ? "#4CAF50" : Colors.error,
            color: "#FFF"
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Patients;
